/* 
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {CSSProperties, ReactNode, useEffect, useState} from 'react';
import {Input, MenuItem, Select} from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import {makeStyles} from '@material-ui/core/styles';
import {
  dlsAlt,
  dlsColor,
  dlsTypography
} from '../../theme/theme';
import styles from './Dropdown.module.scss';

// defines the prop definitions for the dropdown option
export interface Options {
  label: string;
  childPages?: Options[];
  id: string;
};

export interface DropdownGroupProps {
  groups: Options[];
  onChange?: (newValue: string) => void;
  value?: string;
  selected?: string;
  required?: boolean;
  label?: string;
  fieldLabel?: string | ReactNode;
  hasError?: boolean;
  errorText?: string;
  useDisabledStyles?: boolean;
  disabled?: boolean;
  isAlternateStyle?: boolean;
  placeholder?: string;
  style?: CSSProperties;
}

type BackgroundStyles = {
  disabled?: boolean,
  useDisabledStyles?: boolean
};

type DropdownCSSProps = {
  hasError?: boolean,
  useDisabledStyles?: boolean,
  disabled?: boolean,
  fieldLabel?: boolean
}

// defines the styles definition for the Dropdown Component.
export const useStyles = makeStyles(theme => {
  const getBackgroundColor = (disabled: boolean, useDisabledStyles: boolean, isRoot: boolean) => {
    if (disabled && useDisabledStyles) {
      return dlsAlt('components', 'dropdown', 'disabledBackground');
    }
    return isRoot ? 'transparent' : 'inherit';
  };
  return {
    paper : {
      borderRadius : '0px',
      marginTop    : '4px',
      padding      : '0px',
      overflowY    : 'auto',
      textOverflow : 'ellipsis'
    },
    formControl : {
      borderBottom : ({hasError}: { hasError: boolean; }) => (
        hasError ? 'none' : `1px solid ${dlsColor('grey5')}`
      ),
      '& .MuiInputBase-root' : {
        backgroundColor :
          ({disabled, useDisabledStyles}: DropdownCSSProps) => getBackgroundColor(disabled, useDisabledStyles, true),
        width     : '100%',
        marginTop : 0,
        border    : ({hasError}: { hasError: boolean; }) => (
          hasError ? `solid 2px ${dlsColor('caRed')}` : 'none'
        )
      }
    },
    errorText : {
      color : `${dlsColor('caRed')}`
    },
    list : {
      paddingBottom : '0px'
    },
    select : {
      backgroundColor :
        ({disabled, useDisabledStyles}: DropdownCSSProps) => getBackgroundColor(disabled, useDisabledStyles, false),
      padding   : '12px 24px 11px 7px !important',
      '&:focus' : {
        backgroundColor : 'transparent'
      }
    },
    label : {
      ...dlsAlt('components', 'dropdown'),
      color         : dlsColor('grey3'),
      marginTop     : '2px',
      textTransform : ({upperCase}: { upperCase: boolean }) => (
        upperCase ? dlsAlt('components', 'dropdown').textTransform : 'none'
      ),
      letterSpacing : ({upperCase}: { upperCase: boolean }) => (
        upperCase ? dlsAlt('components', 'dropdown').letterSpacing : 'normal'
      )
    },
    root : {
      paddingLeft : ({isAlternateStyle}: {isAlternateStyle: boolean}) => isAlternateStyle ? '0px !important': '17px !important',
      '&:hover'   : {
        backgroundColor : ({isAlternateStyle}: {isAlternateStyle: boolean}) => isAlternateStyle ? dlsColor('paleBlue')
          : dlsAlt('components', 'icon', 'selected')
      },
      '&:selected' : {
        backgroundColor : ({isAlternateStyle}: {isAlternateStyle: boolean}) => isAlternateStyle ? `${dlsColor('paleBlue')} !important` : `${dlsAlt('components', 'icon', 'selected')} !important`
      }
    },
    selectWrapper : {
      backgroundColor : dlsColor('white')
    },
    selected : {
      backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`
    },
    optionHeader : {
      padding       : '17px !important',
      textTransform : 'uppercase',
      ...dlsAlt('typography', 'cta3')
    },
    altOptionHeader : {
      ...dlsTypography('body2'),
      color         : dlsColor('grey2'),
      borderBottom  : `1px solid ${dlsColor('grey5')}`,
      borderTop     : `1px solid ${dlsColor('grey5')}`,
      padding       : '14px !important',
      fontSize      : '14px',
      lineHeight    : '20px',
      textTransform : 'uppercase'
    },
    option : {
      padding       : '17px !important',
      ...dlsAlt('components', 'dropdown'),
      color         : dlsColor('grey3'),
      textTransform : ({upperCase}: {upperCase: boolean}) => (
        upperCase ? 'uppercase' : 'none'
      ),
      letterSpacing : ({upperCase}: {upperCase: boolean}) => (
        upperCase ? dlsAlt('components', 'dropdown').letterSpacing : 'normal'
      ),
      '&[data-focus="true"]' : {
        backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`
      }
    },
    altOption : {
      ...dlsTypography('body2'),
      color      : dlsColor('grey1'),
      padding    : '14px 0px !important',
      fontSize   : '14px',
      lineHeight : '20px'
    },
    fieldLabel : {
      color        : dlsColor('grey2'),
      marginBottom : '8px',
      ...dlsTypography('body3')
    },
    requiredAsterisk : {
      color  : dlsColor('aaBlue'),
      margin : '0 2px'
    },
    flexContainer : {
      display        : 'flex',
      justifyContent : 'space-between',
      alignItems     : 'flex-start'
    }
  };
});

/**
 * 
 * Defines the Dropdown component. This component is reusable and can be custom
 * rendered with props.
 *
 *   <DropdownGroup groups={[]}
 *     fieldLabel='Options'
 *     value={selectedVal}
 *     onChange={setSelectedVal} /> [Field Label]
 *
 * ```
 */
const DropdownGroup = ({
  groups=[],
  onChange,
  value,
  required,
  fieldLabel,
  hasError,
  useDisabledStyles,
  disabled,
  isAlternateStyle = false,
  placeholder,
  ...props
}: DropdownGroupProps) => {
  const classes  = useStyles({
    useDisabledStyles,
    fieldLabel,
    hasError,
    disabled
  });
  
  const [selectedOption, setSelectedOption] = useState<string | null>(value || null);

  useEffect(() => {
    setSelectedOption(value);
  }, [value]);

  function handleSelectChange(selectedOptionId) {
    setSelectedOption(selectedOptionId);
    onChange?.(selectedOptionId);
  }

  const renderPlaceholder = () => (
    <span style={{color: dlsColor('grey4')}}>{placeholder}</span>
  );

  const renderSelected = () => selected => {
    let selectedLabel = '';
    groups?.filter(({childPages}) => {
      const selectedPage = childPages?.find(({id}) => id === selected);
      if (selectedPage) {
        selectedLabel =  selectedPage.label;
      }
    });
    return (
      <div className={styles.selectWrapper}>
        {<span>{selectedLabel}</span>}
      </div>
    );
  };

  function getSelectionOptions() {
    return groups.map(function({label: groupLabel, childPages}, index: number) {
      return (
        <div key={`group-${index}-${groupLabel}`}>
          {childPages?.length >= 1 && (
            <optgroup className={isAlternateStyle ? classes.altOptionHeader : classes.optionHeader}
              label={groupLabel} />
          )}
          {childPages?.map(({id, label}, keyIndex: Number) =>
            <MenuItem key={`option-${id}-${keyIndex}`}
              value={id}
              onClick={function() { handleSelectChange(id); }}
              selected={selectedOption === id}
              classes={{
                root     : classes.root,
                selected : classes.selected
              }}>
              <div className={isAlternateStyle ? classes.altOption : classes.option}>
                <span className={isAlternateStyle ? classes.altOption : classes.option}>
                  {label}
                </span>
              </div>
            </MenuItem>
          )}
        </div>
      );
    });
  }
  
  return (
    <div>
      <FormControl className={classes.formControl}>
        {fieldLabel && (
          <div className={classes.flexContainer}>
            <div className={classes.fieldLabel}>
              {fieldLabel}
              {required && <span className={classes.requiredAsterisk}>*</span>}
            </div>
          </div>
        )}
        <div className={classes.selectWrapper}>
          <Select classes={{select: classes.select}}
            disabled={disabled}
            error={hasError}
            displayEmpty // Required to show placeholder when the value is empty
            renderValue={selectedOption ? renderSelected() : renderPlaceholder}
            value={selectedOption || ''}
            input={<Input />}
            inputProps={{
              name : value,
              required
            }}
            MenuProps={{
              anchorOrigin : {
                vertical   : 'bottom',
                horizontal : -4
              },
              transformOrigin : {
                vertical   : 'top',
                horizontal : 'left'
              },
              getContentAnchorEl : null,
              classes            : {
                paper : classes.paper,
                list  : classes.list
              }
            }}
            className={styles.select}
            {...props}>
            {getSelectionOptions()}
          </Select>
        </div>
      </FormControl>
    </div>
  );
};

export default DropdownGroup;
